/**
 * Central chart display formatting (labels + zodiac degrees + scalar numbers).
 *
 * Name modes control text beside symbols. Symbols always display when available.
 * Degree ceiling rules (intentional):
 * - True sign comes only from raw absolute longitude (or provided sign).
 * - Display ceiling/carry may reach 30° in the current true sign.
 * - Display never rolls the sign forward by itself.
 *
 * Entity naming/symbol precedence: canonical metadata (entity_meta / sign_meta /
 * aspect_meta, supplied by the server from astro's entity registry) always wins
 * over the legacy chart payload's own name/symbol fields, which win over an
 * empty/safe fallback. There is no local planet/sign/aspect naming or symbol
 * table here -- astro is the single source of truth. The one exception is
 * angles: astro's angle full_name is prose ("Ascendant") and its symbol is
 * intentionally null, neither of which should replace the established
 * ASC/MC/DSC/IC code rendering, so formatAngleLabel never consults metadata.
 */

import { normalizeChartDisplaySettings, DEFAULT_CHART_DISPLAY_SETTINGS } from './chartDisplaySettings.js';

const SIGN_ORDER = [
  'Aries',
  'Taurus',
  'Gemini',
  'Cancer',
  'Leo',
  'Virgo',
  'Libra',
  'Scorpio',
  'Sagittarius',
  'Capricorn',
  'Aquarius',
  'Pisces',
];

const EPS = 1e-9;

function resolveSettings(settings) {
  return normalizeChartDisplaySettings(settings || DEFAULT_CHART_DISPLAY_SETTINGS);
}

/**
 * Rendering-time view of settings for expanded contexts (headings, card
 * captions, report titles) where abbreviated entity names are not
 * appropriate, as opposed to compact contexts (lists, tables, dense chart
 * rows) which honor the stored name mode as-is.
 *
 * Converts short -> full for planet/sign/aspect name modes; full and none
 * pass through unchanged. Never mutates or persists the stored settings --
 * callers pass the result straight into the existing formatter functions
 * for a single render, exactly like any other settings object.
 *
 * This is the one place the short->full downgrade rule lives; no component
 * should special-case "is this heading/caption context" naming logic
 * itself.
 *
 * @param {Record<string, unknown>} settings
 * @returns {ReturnType<typeof resolveSettings>}
 */
export function forExpandedContext(settings) {
  const resolved = resolveSettings(settings);
  return {
    ...resolved,
    planetNameMode: resolved.planetNameMode === 'short' ? 'full' : resolved.planetNameMode,
    signNameMode: resolved.signNameMode === 'short' ? 'full' : resolved.signNameMode,
    aspectNameMode: resolved.aspectNameMode === 'short' ? 'full' : resolved.aspectNameMode,
  };
}

function titleCaseName(name) {
  if (!name) {
    return '';
  }
  return String(name)
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Split an optional symbol and a name-mode text into independent parts.
 * Symbols always show when present; name mode is full | short | none.
 * shortText defaults to fullName when not given -- never a truncation.
 * When symbol and name text are identical (e.g. an angle code), the name
 * is dropped so the symbol isn't shown twice.
 *
 * @returns {{ name: string, symbol: string }}
 */
function splitSymbolAndName(symbol, fullName, mode, shortText) {
  const sym = symbol || '';
  let nameText = '';
  if (mode === 'short') {
    nameText = shortText != null ? shortText : (fullName || '');
  } else if (mode === 'full') {
    nameText = fullName || '';
  }

  if (!sym) {
    return { name: nameText || fullName || '', symbol: '' };
  }
  if (nameText && sym === nameText) {
    return { name: '', symbol: sym };
  }
  return { name: nameText, symbol: sym };
}

/**
 * Combine an optional symbol with a name-mode text into one display string.
 * When a name is shown, name comes before symbol: "Moon ☾".
 */
function joinSymbolAndName(symbol, fullName, mode, shortText) {
  const parts = splitSymbolAndName(symbol, fullName, mode, shortText);
  if (parts.name && parts.symbol) {
    return `${parts.name} ${parts.symbol}`;
  }
  return parts.symbol || parts.name || '';
}

/**
 * Ceil toward +∞ for display units when any fractional remainder exists.
 * Exact integers (within EPS) stay unchanged.
 */
export function ceilDisplayUnit(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return 0;
  }
  const nearestInt = Math.round(n);
  if (Math.abs(n - nearestInt) <= EPS) {
    return nearestInt;
  }
  return Math.ceil(n - EPS);
}

export function signIndexFromLongitude(longitude) {
  const normalized = ((Number(longitude) % 360) + 360) % 360;
  return Math.floor(normalized / 30) % 12;
}

export function degreeWithinSignFromLongitude(longitude) {
  const normalized = ((Number(longitude) % 360) + 360) % 360;
  return normalized % 30;
}

export function resolveTrueSign(position) {
  if (position?.longitude != null && Number.isFinite(Number(position.longitude))) {
    return SIGN_ORDER[signIndexFromLongitude(position.longitude)];
  }
  const named = titleCaseName(position?.sign || position?.moon_sign || '');
  if (SIGN_ORDER.includes(named)) {
    return named;
  }
  return named || '';
}

export function resolveDegreeWithinSign(position) {
  if (position?.longitude != null && Number.isFinite(Number(position.longitude))) {
    return degreeWithinSignFromLongitude(position.longitude);
  }
  const degree = position?.sign_degree ?? position?.moon_sign_degree;
  if (degree != null && Number.isFinite(Number(degree))) {
    return Number(degree);
  }
  return 0;
}

function resolvePlanetNameSymbol(planet) {
  const meta = planet?.entity_meta;
  const fullName = meta?.full_name ?? planet?.name ?? '';
  const shortText = meta?.short_name ?? fullName;
  const symbol = meta?.symbol ?? planet?.symbol ?? '';
  // Canonical metadata's canonical_id is authoritative; falling back to the
  // display name only for legacy payloads with no entity_meta at all. This
  // is never a symbol/casing heuristic -- astroTypography.js does a single
  // case-insensitive compare against "sun"/"moon" so both the canonical id
  // ("sun") and the legacy display name ("Sun") resolve identically.
  const canonicalId = meta?.canonical_id ?? planet?.name ?? '';
  return { fullName, shortText, symbol, canonicalId };
}

export function formatPlanetLabel(planet, settings) {
  const mode = resolveSettings(settings).planetNameMode;
  const { fullName, shortText, symbol } = resolvePlanetNameSymbol(planet);
  return joinSymbolAndName(symbol, fullName, mode, shortText);
}

/**
 * Same entity/name resolution as formatPlanetLabel, split into independent
 * name/symbol parts for components that style them separately. Includes
 * canonicalId so AstroLabel can select Sun/Moon-specific symbol scale.
 * @returns {{ name: string, symbol: string, canonicalId: string }}
 */
export function formatPlanetLabelParts(planet, settings) {
  const mode = resolveSettings(settings).planetNameMode;
  const { fullName, shortText, symbol, canonicalId } = resolvePlanetNameSymbol(planet);
  return { ...splitSymbolAndName(symbol, fullName, mode, shortText), canonicalId };
}

function resolveSignNameSymbol(signNameOrRecord) {
  let fullName = '';
  let shortText;
  let symbol = '';

  if (typeof signNameOrRecord === 'string') {
    fullName = titleCaseName(signNameOrRecord);
  } else if (signNameOrRecord && typeof signNameOrRecord === 'object') {
    const meta = signNameOrRecord.sign_meta;
    fullName = meta?.full_name
      ?? resolveTrueSign(signNameOrRecord)
      ?? titleCaseName(signNameOrRecord.sign || signNameOrRecord.moon_sign || '');
    shortText = meta?.short_name;
    symbol = meta?.symbol
      ?? signNameOrRecord.sign_symbol
      ?? signNameOrRecord.moon_sign_symbol
      ?? '';
  }

  return { fullName, shortText: shortText ?? fullName, symbol };
}

export function formatSignLabel(signNameOrRecord, settings) {
  const mode = resolveSettings(settings).signNameMode;
  const { fullName, shortText, symbol } = resolveSignNameSymbol(signNameOrRecord);
  return joinSymbolAndName(symbol, fullName, mode, shortText);
}

/**
 * Same entity/name resolution as formatSignLabel, split into independent
 * name/symbol parts for components that style them separately.
 * @returns {{ name: string, symbol: string }}
 */
export function formatSignLabelParts(signNameOrRecord, settings) {
  const mode = resolveSettings(settings).signNameMode;
  const { fullName, shortText, symbol } = resolveSignNameSymbol(signNameOrRecord);
  return splitSymbolAndName(symbol, fullName, mode, shortText);
}

function resolveAspectNameSymbol(aspectName, aspectSymbol, aspectMeta) {
  const fullName = aspectMeta?.full_name ?? String(aspectName || '');
  const shortText = aspectMeta?.short_name ?? fullName;
  const symbol = aspectMeta?.symbol ?? aspectSymbol ?? '';
  return { fullName, shortText, symbol };
}

export function formatAspectName(aspectName, settings, aspectSymbol = '', aspectMeta = null) {
  const mode = resolveSettings(settings).aspectNameMode;
  const { fullName, shortText, symbol } = resolveAspectNameSymbol(aspectName, aspectSymbol, aspectMeta);
  if (mode === 'none') {
    return symbol || fullName;
  }
  return joinSymbolAndName(symbol, fullName, mode, shortText);
}

/**
 * Same entity/name resolution as formatAspectName, split into independent
 * name/symbol parts for components that style them separately.
 * @returns {{ name: string, symbol: string }}
 */
export function formatAspectNameParts(aspectName, settings, aspectSymbol = '', aspectMeta = null) {
  const mode = resolveSettings(settings).aspectNameMode;
  const { fullName, shortText, symbol } = resolveAspectNameSymbol(aspectName, aspectSymbol, aspectMeta);
  return splitSymbolAndName(symbol, fullName, mode, shortText);
}

export function formatAspectLabel(aspect, settings) {
  const resolved = resolveSettings(settings);
  const a = formatPlanetLabel(
    { name: aspect?.point_a, symbol: aspect?.point_a_symbol, entity_meta: aspect?.point_a_meta },
    resolved,
  );
  const aspectLabel = formatAspectName(
    aspect?.aspect,
    resolved,
    aspect?.aspect_symbol || '',
    aspect?.aspect_meta,
  );
  const b = formatPlanetLabel(
    { name: aspect?.point_b, symbol: aspect?.point_b_symbol, entity_meta: aspect?.point_b_meta },
    resolved,
  );
  return `${a} ${aspectLabel} ${b}`.trim();
}

/**
 * Angles (ASC/MC/DSC/IC and the less common codes) always use the existing
 * payload's own name/symbol fields, never entity_meta -- astro's angle
 * full_name is prose ("Ascendant") and its symbol is intentionally null,
 * neither of which should change the established code-based rendering.
 */
export function formatAngleLabel(angle, settings) {
  const mode = resolveSettings(settings).planetNameMode;
  const name = angle?.name || '';
  const symbol = angle?.symbol || '';
  return joinSymbolAndName(symbol, name, mode, name);
}

/**
 * The angle codes astro's natal chart pipeline currently calculates and
 * emits (see astro/core/charts/event_chart.py's angle_ids). Used only as a
 * narrow fallback for aspect endpoints from old cached payloads that
 * predate point_a_meta/point_b_meta -- never a general "looks like an
 * angle" heuristic. Canonical metadata is always checked first.
 */
const LEGACY_ANGLE_CODES = new Set(['ASC', 'MC', 'DSC', 'IC']);

/**
 * Classify a single aspect endpoint (or top-level angle/planet record) as
 * an angle, independent of which side of the aspect it's on.
 *
 * Canonical metadata (entity_meta.category === 'angle') is the primary and
 * authoritative source. Only when metadata is entirely absent -- an old
 * cached chart persisted before entity_meta enrichment existed -- does this
 * fall back to matching the display name against the known angle codes. A
 * name is never classified as an angle merely because it's uppercase (e.g.
 * "ASC" the exact code matches; an unrelated uppercase acronym would not).
 *
 * @param {string | null | undefined} name
 * @param {{ category?: string } | null | undefined} meta
 * @returns {boolean}
 */
export function isAngleEndpoint(name, meta) {
  if (meta && typeof meta === 'object' && meta.category) {
    return meta.category === 'angle';
  }
  return LEGACY_ANGLE_CODES.has(name);
}

/**
 * Format degree-within-sign using ceiling-to-selected-unit rules.
 * May return a degrees value of 30 for the current true sign.
 *
 * @returns {{ degrees: number, minutes: number, seconds: number, text: string }}
 */
export function formatDegreeParts(degreeWithinSign, settings) {
  const resolved = resolveSettings(settings);
  const raw = Math.max(0, Number(degreeWithinSign) || 0);
  const format = resolved.degreeFormat;

  if (format === 'degree') {
    const degrees = ceilDisplayUnit(raw);
    return { degrees, minutes: 0, seconds: 0, text: `${degrees}°` };
  }

  if (format === 'dm') {
    const totalMinutes = ceilDisplayUnit(raw * 60);
    const degrees = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const text = `${degrees}° ${String(minutes).padStart(2, '0')}'`;
    return { degrees, minutes, seconds: 0, text };
  }

  // dms (default fallback for unknown values after normalize)
  const totalSeconds = ceilDisplayUnit(raw * 3600);
  const seconds = totalSeconds % 60;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const minutes = totalMinutes % 60;
  const degrees = Math.floor(totalMinutes / 60);
  const text = `${degrees}° ${String(minutes).padStart(2, '0')}' ${String(seconds).padStart(2, '0')}"`;
  return { degrees, minutes, seconds, text };
}

/**
 * Format a zodiac position. Sign is always from raw longitude / provided sign.
 */
export function formatZodiacPosition(position, settings) {
  const resolved = resolveSettings(settings);
  const signLabel = formatSignLabel(position, resolved);
  const degreeInSign = resolveDegreeWithinSign(position);
  const { text: degreeText } = formatDegreeParts(degreeInSign, resolved);
  if (!signLabel) {
    return degreeText;
  }
  return `${signLabel} ${degreeText}`;
}

/**
 * The degree-within-sign text alone, with no sign name/symbol -- for
 * components that render the sign through a separate structured
 * AstroLabel category="sign" (via formatSignLabelParts) and only need the
 * numeric position text alongside it.
 */
export function formatPositionDegreeText(position, settings) {
  const resolved = resolveSettings(settings);
  const degreeInSign = resolveDegreeWithinSign(position);
  return formatDegreeParts(degreeInSign, resolved).text;
}

export function formatDegreeValue(degreeWithinSign, sign, settings) {
  return formatZodiacPosition(
    {
      sign,
      sign_degree: degreeWithinSign,
    },
    settings,
  );
}

/**
 * Format a non-zodiac scalar numeric value with trailing zeros.
 * Uses valueDecimalPlaces (not zodiac decimalPlaces).
 */
export function formatNumericValue(value, settings) {
  if (value == null || value === '') {
    return '';
  }
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return '';
  }
  const places = resolveSettings(settings).valueDecimalPlaces;
  return n.toFixed(places);
}

/**
 * Compact aspect motion label for lists: app / sep.
 */
export function aspectMotionLabel(aspect) {
  if (aspect?.applying === true) {
    return 'app';
  }
  if (aspect?.separating === true) {
    return 'sep';
  }
  return '';
}

export { SIGN_ORDER };
